

export interface Node {
	id: string,
	x: number,
	y: number,
	w: number,
	h: number,
	fixed?: boolean,
	column?: number,
	row?: number,
}

export interface Edge {
	source: string,
	target: string,
	routingPoints: any[],
	isBidirectional?: boolean,
}

export interface Graph {
	nodes: Node[],
	edges: Edge[],
	width: number,
	height: number,
}

export interface LayoutOptions {
	nodeWidth: number,
	nodeHeight: number,
	horizontalSpacing: number,
	verticalSpacing: number,
	padding: number,
}

const defaultLayoutOptions: LayoutOptions = {
	nodeWidth: 160,
	nodeHeight: 44,
	horizontalSpacing: 80,
	verticalSpacing: 60,
	padding: 30,
};

interface TransitionInfo {
	sourceId: string,
	targetId: string,
	isDefault: boolean,
}

export function layoutNodes(
	graph: Graph,
	transitions: TransitionInfo[],
	options?: LayoutOptions,
) {
	const opts = options || defaultLayoutOptions;

	// Build default-transition chain
	const defaultNext: { [k: string]: string } = {};
	const defaultPrev: { [k: string]: string } = {};

	for (const t of transitions) {
		if (t.isDefault) {
			if (!defaultNext[t.sourceId]) defaultNext[t.sourceId] = t.targetId;
			if (!defaultPrev[t.targetId]) defaultPrev[t.targetId] = t.sourceId;
		}
	}

	// Find backbone chain
	const backboneNodes: string[] = [];
	const backboneSet: { [k: string]: boolean } = {};

	const chainStarts: string[] = [];
	for (const src of Object.keys(defaultNext)) {
		if (!defaultPrev[src]) chainStarts.push(src);
	}

	let backboneStart = chainStarts.length > 0 ? chainStarts[0] : graph.nodes[0]?.id;
	if (graph.nodes.length > 0) {
		const firstId = graph.nodes[0].id;
		if (chainStarts.indexOf(firstId) >= 0) {
			backboneStart = firstId;
		} else if (chainStarts.length === 0) {
			backboneStart = firstId;
		}
	}

	if (backboneStart) {
		let current: string | undefined = backboneStart;
		const visited: { [k: string]: boolean } = {};
		while (current && !visited[current]) {
			visited[current] = true;
			backboneNodes.push(current);
			backboneSet[current] = true;
			current = defaultNext[current];
		}
	}

	// Include stray default-chain tails
	for (const n of graph.nodes) {
		if (!backboneSet[n.id] && defaultPrev[n.id] && backboneSet[defaultPrev[n.id]]) {
			backboneNodes.push(n.id);
			backboneSet[n.id] = true;
		}
	}

	// Connection maps (bidirectional adjacency)
	const neighbors: { [k: string]: string[] } = {};
	for (const t of transitions) {
		if (!neighbors[t.sourceId]) neighbors[t.sourceId] = [];
		if (!neighbors[t.targetId]) neighbors[t.targetId] = [];
		if (neighbors[t.sourceId].indexOf(t.targetId) < 0) neighbors[t.sourceId].push(t.targetId);
		if (neighbors[t.targetId].indexOf(t.sourceId) < 0) neighbors[t.targetId].push(t.sourceId);
	}

	// Grid occupancy: "row,col" -> nodeId
	const grid: { [k: string]: string } = {};
	function isOccupied(row: number, col: number): boolean {
		return !!grid[row + "," + col];
	}
	function occupy(row: number, col: number, id: string) {
		grid[row + "," + col] = id;
	}

	// Placement records
	const nodeRow: { [k: string]: number } = {};
	const nodeCol: { [k: string]: number } = {};
	const placed: { [k: string]: boolean } = {};

	function placeNode(id: string, row: number, col: number) {
		nodeRow[id] = row;
		nodeCol[id] = col;
		placed[id] = true;
		occupy(row, col, id);
	}

	// Place backbone in column 0, one per row, with gaps for branches
	// First pass: figure out how many branch rows each backbone node needs
	const branchesPerBackbone: { [backboneId: string]: string[] } = {};
	const nonBackboneNodes: string[] = [];

	for (const n of graph.nodes) {
		if (!backboneSet[n.id]) nonBackboneNodes.push(n.id);
	}

	for (const branchId of nonBackboneNodes) {
		let bestParent: string | undefined;
		let bestParentIdx = Infinity;

		for (const bId of backboneNodes) {
			const nbrs = neighbors[bId] || [];
			if (nbrs.indexOf(branchId) >= 0) {
				const idx = backboneNodes.indexOf(bId);
				if (idx < bestParentIdx) { bestParent = bId; bestParentIdx = idx; }
			}
		}
		// Indirect: connected to something connected to backbone
		if (!bestParent) {
			const branchNbrs = neighbors[branchId] || [];
			for (const connId of branchNbrs) {
				if (backboneSet[connId]) {
					const idx = backboneNodes.indexOf(connId);
					if (idx < bestParentIdx) { bestParent = connId; bestParentIdx = idx; }
				}
			}
		}
		if (!bestParent && backboneNodes.length > 0) {
			bestParent = backboneNodes[backboneNodes.length - 1];
		}
		if (bestParent) {
			if (!branchesPerBackbone[bestParent]) branchesPerBackbone[bestParent] = [];
			branchesPerBackbone[bestParent].push(branchId);
		}
	}

	// Place backbone with gaps for branches.
	// Each backbone node gets ceil(branches/2) gap rows after it — enough for 2
	// branches per row in cols -1,1, but branches can spread wider if scoring prefers it.
	let currentRow = 0;
	const backboneRowOf: { [backboneId: string]: number } = {};

	for (let bi = 0; bi < backboneNodes.length; bi++) {
		const bId = backboneNodes[bi];
		placeNode(bId, currentRow, 0);
		backboneRowOf[bId] = currentRow;

		const branches = branchesPerBackbone[bId] || [];
		const gapRows = Math.ceil(branches.length / 2);
		currentRow += 1 + gapRows;
	}

	// Total rows allocated so far (used as upper bound for searches)
	const totalRows = currentRow;

	// Now place each branch at the best position considering ALL its connections.
	// The search range covers the entire grid, not just the parent's gap rows,
	// so a branch can sit near a connected node even if it's far from its parent.
	for (let bi = 0; bi < backboneNodes.length; bi++) {
		const bId = backboneNodes[bi];
		const branches = branchesPerBackbone[bId] || [];
		if (branches.length === 0) continue;

		// Max column spread: enough to hold all branches on one row if needed
		const maxSide = Math.max(2, Math.ceil(branches.length / 2));

		// Sort: place branches with more already-placed connections first
		branches.sort((a, b) => {
			const aPlaced = (neighbors[a] || []).filter(n => placed[n]).length;
			const bPlaced = (neighbors[b] || []).filter(n => placed[n]).length;
			return bPlaced - aPlaced;
		});

		for (const branchId of branches) {
			let bestRow = backboneRowOf[bId] + 1;
			let bestCol = 1;
			let bestScore = Infinity;

			// Determine search row range: start from parent gap rows,
			// but extend to cover rows near any connected placed node
			const parentRow = backboneRowOf[bId];
			const gapRows = Math.ceil((branchesPerBackbone[bId] || []).length / 2);
			let searchMinRow = parentRow + 1;
			let searchMaxRow = parentRow + gapRows;

			const nbrs = neighbors[branchId] || [];
			for (const nbrId of nbrs) {
				if (!placed[nbrId]) continue;
				const nbrRow = nodeRow[nbrId];
				// Extend search to rows adjacent to this connection
				if (nbrRow - 1 < searchMinRow) searchMinRow = Math.max(1, nbrRow - 1);
				if (nbrRow + 1 > searchMaxRow) searchMaxRow = Math.min(totalRows - 1, nbrRow + 1);
			}

			for (let r = searchMinRow; r <= searchMaxRow; r++) {
				for (let c = -maxSide; c <= maxSide; c++) {
					if (c === 0) continue;
					if (isOccupied(r, c)) continue;

					let score = 0;
					let hasPlacedNeighbor = false;
					for (const nbrId of nbrs) {
						if (!placed[nbrId]) continue;
						hasPlacedNeighbor = true;
						const dr = Math.abs(r - nodeRow[nbrId]);
						const dc = Math.abs(c - nodeCol[nbrId]);
						score += dr + dc;
					}
					if (!hasPlacedNeighbor) {
						score = Math.abs(c) * 2 + Math.abs(r - parentRow);
					}
					// Slight preference for columns closer to backbone
					score += Math.abs(c) * 0.1;

					if (score < bestScore) {
						bestScore = score;
						bestRow = r;
						bestCol = c;
					}
				}
			}

			placeNode(branchId, bestRow, bestCol);
		}
	}

	// Safety net for any unassigned nodes
	for (const n of graph.nodes) {
		if (!placed[n.id]) {
			while (isOccupied(currentRow, 1)) currentRow++;
			placeNode(n.id, currentRow, 1);
			currentRow++;
		}
	}

	// Determine grid bounds
	let minCol = 0, maxCol = 0, maxRow = 0;
	for (const id of Object.keys(nodeCol)) {
		if (nodeCol[id] < minCol) minCol = nodeCol[id];
		if (nodeCol[id] > maxCol) maxCol = nodeCol[id];
		if (nodeRow[id] > maxRow) maxRow = nodeRow[id];
	}

	// Convert to pixel positions
	const colWidth = opts.nodeWidth + opts.horizontalSpacing;
	const rowHeight = opts.nodeHeight + opts.verticalSpacing;
	const colOffset = -minCol;

	for (const n of graph.nodes) {
		const row = nodeRow[n.id];
		const col = nodeCol[n.id];
		n.x = opts.padding + (col + colOffset) * colWidth;
		n.y = opts.padding + row * rowHeight;
		n.w = opts.nodeWidth;
		n.h = opts.nodeHeight;
		n.column = col;
		n.row = row;
	}

	// Mark bidirectional edges
	const edgeSet: { [k: string]: boolean } = {};
	for (const t of transitions) {
		edgeSet[t.sourceId + "->" + t.targetId] = true;
	}
	for (const e of graph.edges) {
		e.isBidirectional = !!edgeSet[e.target + "->" + e.source];
	}

	// Graph dimensions
	const totalCols = maxCol - minCol + 1;
	graph.width = opts.padding * 2 + totalCols * colWidth;
	graph.height = opts.padding * 2 + (maxRow + 1) * rowHeight;
}
